import React, {useEffect, useMemo, useState} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import TopNav from "../components/TopNav";

import "./ToursAllPage.css"

// import Visa from "../components/Visa";

const TourImage = React.memo(function TourImage({src, alt, id}) {
    const [imgLoading, setImgLoading] = useState(true);
    const [imgError, setImgError] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        // If the source changes, re-enter loading state.
        setImgLoading(true);
        setImgError(false);
    }, [src]);

    function handleClick(){
        console.log(id);
        navigate(`/tour/${id}`)

    }

    return (
        <div className="tpAv_media" onClick={handleClick}>
            {imgLoading && (
                <div className="tpAv_imgOverlay" role="status" aria-live="polite">
                    <span className="tpAv_srOnly">Loading image</span>
                    <div className="tpAv_spinner" aria-hidden="true" />
                </div>
            )}

            <img
                className="tpAv_img"
                src={src}
                alt={alt}
                loading="lazy"
                onLoad={() => setImgLoading(false)}
                onError={() => {
                    setImgLoading(false);
                    setImgError(true);
                }}
            />

            {imgError && (
                <div className="tpAv_imgOverlay" role="status" aria-live="polite">
                    <span className="tpAv_loadingText">Image unavailable</span>
                </div>
            )}
        </div>
    );
});

const ToursAllPage = (props) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // UI-only state (does not affect data fetching/state management logic)
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [minCompleted, setMinCompleted] = useState("");
    const [page, setPage] = useState(1);

    // Pagination: fixed size (UI requirement: paginated, but no page-size control)
    const pageSize = 8;

    // Client-side cache (1 hour)
    const TOURS_CACHE_KEY = "tpAv_tours_cache_v1";
    const TOURS_CACHE_TTL_MS = 60 * 60 * 1000;

    useEffect(() => {
      getTours();
      return () => {}
    }, [])


    const getTours = async ({ bypassCache = false } = {}) => {
        try {
            // Serve cached tours when fresh to avoid unnecessary network fetch.
            if (!bypassCache) {
                try {
                    const raw = localStorage.getItem(TOURS_CACHE_KEY);
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        const ts = Number(parsed?.ts);
                        const cachedTours = parsed?.tours;
                        if (Array.isArray(cachedTours) && ts && Date.now() - ts < TOURS_CACHE_TTL_MS) {
                            setError(null);
                            setData(cachedTours);
                            return;
                        }
                    }
                } catch (e) {
                    // Ignore cache parse/access errors and fall back to network.
                }
            }

            console.log("getTours()")
            setLoading(true);

            const response = await axios.get("/api/tours/get_all", {
            params: { _t: Date.now() }
            });                     
            console.log(response.data.tours)
            setData(response.data.tours);

            try {
                localStorage.setItem(
                    TOURS_CACHE_KEY,
                    JSON.stringify({ts: Date.now(), tours: response.data.tours})
                );
            } catch (e) {
                // Ignore cache write errors.
            }
        }
        catch (e) {
            console.log(e)
            setError(e);
            setData([]); 
        }
        finally{
            setLoading(false); // Stop loading after any error
        }
    }

    const isDark = !!props.darkmode;

    const toursWithImgIndex = useMemo(() => {
        const list = Array.isArray(data) ? data : [];

        const lmap =  list.map((t, i) => ({...t, __imgIndex: i + 1}));

        return lmap;
    }, [data]);

    // Responsive column rules:
    // - < 10 tours: 2-across on desktop
    // - >= 10 tours: 4-across on desktop
    // - always collapses to 2 then 1 on smaller breakpoints
    const gridColsLg = toursWithImgIndex.length >= 10 ? 4 : 2;
    const gridColsMd = 2;
    const gridColsSm = 1;

    const filteredAndSorted = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        const minC = minCompleted === "" ? null : Number(minCompleted);

        const filtered = toursWithImgIndex.filter((tour) => {
            const legsCompleted = Number(tour.legs_completed || 0);
            const legsTotal = Number(tour.legs_total || 0);
            const started = legsCompleted > 0;
            const completed = legsTotal > 0 && legsCompleted >= legsTotal;

            if (q) {
                const name = String(tour.name || "").toLowerCase();
                const summary = String(tour.summary || "").toLowerCase();
                const descrip = String(tour.descrip || "").toLowerCase();
                if (!name.includes(q) && !summary.includes(q) && !descrip.includes(q)) return false;
            }

            if (statusFilter === "started" && !started) return false;
            if (statusFilter === "notStarted" && started) return false;
            if (statusFilter === "completed" && !completed) return false;

            if (minC !== null && !Number.isNaN(minC) && legsCompleted < minC) return false;

            return true;
        });

        // Default sort (UI-only): by name, then id.
        const sorted = [...filtered].sort((a, b) => {
            const cmp = String(a.name || "").localeCompare(String(b.name || ""));
            if (cmp !== 0) return cmp;
            return String(a.id || "").localeCompare(String(b.id || ""));
        });

        return sorted;
    }, [toursWithImgIndex, query, statusFilter, minCompleted]);

    useEffect(() => {
        // Reset pagination when filters/sort change.
        setPage(1);
    }, [query, statusFilter, minCompleted]);

    const pageCount = Math.max(1, Math.ceil(filteredAndSorted.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const pageStart = (safePage - 1) * pageSize;
    const pageItems = filteredAndSorted.slice(pageStart, pageStart + pageSize);

    return(

        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            <div className={`tpAv_root ${isDark ? "tpAv_dark" : "tpAv_light"}`}>
                <div className="tpAv_page">
                    {/* <header className="tpAv_header">
                        <h1 className="tpAv_title">Tours</h1>
                        <p className="tpAv_subtitle">
                            Browse available tours and track your progress. Tours you’ve started are highlighted; completed tours have special emphasis.
                        </p>
                    </header> */}

                    {error && (
                        <div className="tpAv_error" role="alert">
                            Unable to load tours.
                        </div>
                    )}

                    {loading && (
                        <div className="tpAv_loadingOverlay" role="status" aria-live="polite">
                            <span className="tpAv_srOnly">Loading tours</span>
                            <div className="tpAv_spinner" aria-hidden="true" />
                            <div className="tpAv_loadingText">Fetching tours…</div>
                        </div>
                    )}

                    <section className="tpAv_controls" aria-label="Tour list controls">
                        <div className="tpAv_controlsGrid">
                            <div className="tpAv_field">
                                <label className="tpAv_label" htmlFor="tpAv_query">Search</label>
                                <input
                                    id="tpAv_query"
                                    className="tpAv_input"
                                    type="search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search by name, summary, or description"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="tpAv_field">
                                <label className="tpAv_label" htmlFor="tpAv_status">Status</label>
                                <select
                                    id="tpAv_status"
                                    className="tpAv_select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All</option>
                                    <option value="started">Started</option>
                                    <option value="completed">Completed</option>
                                    <option value="notStarted">Not started</option>
                                </select>
                            </div>

                            <div className="tpAv_field">
                                <label className="tpAv_label" htmlFor="tpAv_minCompleted">Min legs completed</label>
                                <input
                                    id="tpAv_minCompleted"
                                    className="tpAv_input"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    value={minCompleted}
                                    onChange={(e) => setMinCompleted(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>

                        <div className="tpAv_metaRow">
                            <div className="tpAv_count" aria-live="polite">
                                Showing {filteredAndSorted.length} of {toursWithImgIndex.length} tours
                            </div>

                            <button
                                type="button"
                                className="tpAv_refreshBtn"
                                onClick={() => getTours({ bypassCache: true })}
                                disabled={loading}
                                aria-label="Refresh tours"
                                title="Refresh tours from server"
                            >
                                {loading ? "Refreshing…" : "↻ Refresh"}
                            </button>

                            <div className="tpAv_hint" id="tpAv_hint">
                                Tab order follows the visual layout.
                            </div>
                        </div>
                    </section>

                    <main className="tpAv_main" aria-busy={loading ? "true" : "false"}>
                        <section
                            className="tpAv_grid"
                            style={{
                                "--tpAv-colsLg": gridColsLg,
                                "--tpAv-colsMd": gridColsMd,
                                "--tpAv-colsSm": gridColsSm,
                            }}
                            aria-label="Tours"
                        >
                            {pageItems.map((tour, index) => {
                                const legsCompleted = Number(tour.legs_completed || 0);
                                const legsTotal = Number(tour.legs_total || 0);
                                const started = legsCompleted > 0;
                                const completed = legsTotal > 0 && legsCompleted >= legsTotal;

                                const cardClass = [
                                    "tpAv_card",
                                    started ? "tpAv_started" : "",
                                    completed ? "tpAv_completed" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");

                                return (
                                    <article
                                        key={tour.id ?? `${tour.name}-${index}`}
                                        className={cardClass}
                                        style={{animationDelay: `${Math.min(index, 10) * 35}ms`}}
                                    >
                                        <TourImage
                                            // src={`/tours/tour_${tour.id}.jpg`}
                                            src={`${process.env.PUBLIC_URL}/tours/tour_${tour.id}.jpg`}
                                            alt={tour.name}
                                            id={tour.id}
                                        />

                                        <div className="tpAv_body">
                                            <div className="tpAv_cardHeader">
                                                <h2 className="tpAv_cardTitle">{tour.name}</h2>
                                                <div className="tpAv_badges" aria-label="Tour status">
                                                    {legsCompleted > 0 && (
                                                        <span className="tpAv_badge">Legs: {tour.legs_completed} / {tour.legs_total}</span>
                                                    )}
                                                    {completed && (
                                                        <span className="tpAv_badge tpAv_badgeCompleted">Completed</span>
                                                    )}
                                                    {!completed && started && (
                                                        <span className="tpAv_badge tpAv_badgeStarted">Started</span>
                                                    )}
                                                </div>
                                            </div>


                                        </div>
                                    </article>
                                );
                            })}
                        </section>
                    </main>

                    <nav className="tpAv_pagination" aria-label="Pagination">
                        <button
                            type="button"
                            className="tpAv_pageBtn"
                            onClick={() => setPage(1)}
                            disabled={safePage <= 1}
                        >
                            First
                        </button>
                        <button
                            type="button"
                            className="tpAv_pageBtn"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={safePage <= 1}
                            aria-label="Previous page"
                        >
                            Prev
                        </button>

                        {(() => {
                            // Accessible pagination: show a compact window around the current page,
                            // plus first/last when available.
                            const windowSize = 2;
                            const pages = [];

                            const push = (p) => {
                                if (!pages.includes(p) && p >= 1 && p <= pageCount) pages.push(p);
                            };

                            push(1);
                            for (let p = safePage - windowSize; p <= safePage + windowSize; p++) push(p);
                            push(pageCount);

                            pages.sort((a, b) => a - b);

                            const out = [];
                            for (let i = 0; i < pages.length; i++) {
                                const p = pages[i];
                                const prev = pages[i - 1];
                                if (i > 0 && p - prev > 1) {
                                    out.push(
                                        <span key={`gap-${prev}-${p}`} className="tpAv_hint" aria-hidden="true">
                                            …
                                        </span>
                                    );
                                }
                                out.push(
                                    <button
                                        key={p}
                                        type="button"
                                        className={`tpAv_pageBtn ${p === safePage ? "tpAv_current" : ""}`}
                                        onClick={() => setPage(p)}
                                        aria-current={p === safePage ? "page" : undefined}
                                        aria-label={`Page ${p}`}
                                    >
                                        {p}
                                    </button>
                                );
                            }
                            return out;
                        })()}

                        <button
                            type="button"
                            className="tpAv_pageBtn"
                            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                            disabled={safePage >= pageCount}
                            aria-label="Next page"
                        >
                            Next
                        </button>
                        <button
                            type="button"
                            className="tpAv_pageBtn"
                            onClick={() => setPage(pageCount)}
                            disabled={safePage >= pageCount}
                        >
                            Last
                        </button>
                    </nav>
                </div>
            </div>
        </React.Fragment>
    )

}
export default ToursAllPage;
